import { useState, useCallback, useEffect, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Application, FunctionalArea } from '../types';
import { FunctionalAreasQuery, AdditionalApplicationsQuery } from '../queries';
import { APPLICATION_PAGE_SIZE } from '../utils/dataLeadUtils';
import { RoleId } from '../../../insights/types/RoleTypes';

interface FunctionalAreaTransition {
  isTransitioning: boolean;
  targetArea: string | null;
  currentArea: string;
  dataReady: boolean;
}

export interface UseFunctionalAreaManagerReturn {
  // State
  selectedFunctionalArea: string;
  activeFunctionalAreaApps: Application[];
  completeFunctionalAreasData: FunctionalArea[];
  fetchedFunctionalAreas: Set<string>;
  functionalAreaTransition: FunctionalAreaTransition;
  isLoadingAdditionalPages: boolean;
  isFetchingApplicationsForArea: boolean;

  // Transition management
  pendingSwitchToArea: string;
  showSwitchConfirmDialog: boolean;

  // Actions
  performFunctionalAreaTransition: (newFunctionalArea: string) => void;
  handleFunctionalAreaChangeWithConfirmation: (
    newFunctionalArea: string,
  ) => void;
  confirmFunctionalAreaSwitch: () => void;
  cancelFunctionalAreaSwitch: () => void;
  fetchApplicationsForFunctionalArea: (
    functionalAreaName: string,
  ) => Promise<void>;
  fetchAdditionalApplicationsForArea: (
    functionalAreaName: string,
  ) => Promise<void>;

  // Setters for external state management
  setSelectedFunctionalArea: (area: string) => void;
  setActiveFunctionalAreaApps: (apps: Application[]) => void;
  setCompleteFunctionalAreasData: (
    data: FunctionalArea[] | ((prev: FunctionalArea[]) => FunctionalArea[]),
  ) => void;
}

interface UseFunctionalAreaManagerProps {
  dataLeadManager: {
    hasRowChanges: (applicationId: number) => boolean;
    initializeDataLeadAssignments: (apps: Application[]) => void;
  };
  data?: any; // GraphQL data
}

/**
 * Custom hook to manage functional area state
 * @param param0 - Props for the hook
 * @returns Functional area state and actions
 */
export const useFunctionalAreaManager = ({
  dataLeadManager,
  data,
}: UseFunctionalAreaManagerProps): UseFunctionalAreaManagerReturn => {
  // State
  const [selectedFunctionalArea, setSelectedFunctionalArea] =
    useState<string>('');
  const [activeFunctionalAreaApps, setActiveFunctionalAreaApps] = useState<
    Application[]
  >([]);
  const [fetchedFunctionalAreas, setFetchedFunctionalAreas] = useState<
    Set<string>
  >(new Set());
  const [completeFunctionalAreasData, setCompleteFunctionalAreasData] =
    useState<FunctionalArea[]>([]);
  const [isLoadingAdditionalPages, setIsLoadingAdditionalPages] =
    useState<boolean>(false);
  const [isFetchingApplicationsForArea, setIsFetchingApplicationsForArea] =
    useState<boolean>(false);

  // Transition state
  const [functionalAreaTransition, setFunctionalAreaTransition] =
    useState<FunctionalAreaTransition>({
      isTransitioning: false,
      targetArea: null,
      currentArea: '',
      dataReady: false,
    });

  // Dialog state
  const [showSwitchConfirmDialog, setShowSwitchConfirmDialog] =
    useState<boolean>(false);
  const [pendingSwitchToArea, setPendingSwitchToArea] = useState<string>('');

  // GraphQL queries
  const [fetchApplications] = useLazyQuery(FunctionalAreasQuery, {
    errorPolicy: 'all',
  });

  const [fetchAdditionalApplications] = useLazyQuery(
    AdditionalApplicationsQuery,
    {
      errorPolicy: 'all',
    },
  );

  // Consolidated function to handle all aspects of functional area transitions
  const performFunctionalAreaTransition = useCallback(
    (newFunctionalArea: string) => {
      // Step 1: Reset state and update area immediately
      setActiveFunctionalAreaApps([]);

      // Step 2: Start transition and set loading immediately if area hasn't been fetched
      if (!fetchedFunctionalAreas.has(newFunctionalArea)) {
        setIsFetchingApplicationsForArea(true);
      }

      setFunctionalAreaTransition({
        isTransitioning: true,
        targetArea: newFunctionalArea,
        currentArea: selectedFunctionalArea,
        dataReady: false,
      });

      // Step 3: Set the selected functional area immediately so banner shows
      setSelectedFunctionalArea(newFunctionalArea);
    },
    [selectedFunctionalArea, fetchedFunctionalAreas],
  );

  // Function to handle functional area changes with confirmation for pending changes
  const handleFunctionalAreaChangeWithConfirmation = useCallback(
    (newFunctionalArea: string) => {
      // Check if there are any pending changes across all applications
      const hasPendingChanges = activeFunctionalAreaApps.some(app =>
        dataLeadManager.hasRowChanges(app.applicationId),
      );

      if (hasPendingChanges) {
        // Show confirmation dialog
        setPendingSwitchToArea(newFunctionalArea);
        setShowSwitchConfirmDialog(true);
      } else {
        // No pending changes, switch immediately
        performFunctionalAreaTransition(newFunctionalArea);
      }
    },
    [
      activeFunctionalAreaApps,
      dataLeadManager,
      performFunctionalAreaTransition,
    ],
  );

  // Function to confirm the functional area switch and discard changes
  const confirmFunctionalAreaSwitch = useCallback(() => {
    // Reset any pending changes by reinitializing data for the current area
    if (activeFunctionalAreaApps.length > 0) {
      dataLeadManager.initializeDataLeadAssignments(activeFunctionalAreaApps);
    }
    performFunctionalAreaTransition(pendingSwitchToArea);
    setShowSwitchConfirmDialog(false);
    setPendingSwitchToArea('');
  }, [
    activeFunctionalAreaApps,
    dataLeadManager,
    performFunctionalAreaTransition,
    pendingSwitchToArea,
  ]);

  // Function to cancel the functional area switch
  const cancelFunctionalAreaSwitch = useCallback(() => {
    setShowSwitchConfirmDialog(false);
    setPendingSwitchToArea('');
  }, []);

  // Function to fetch additional application pages
  const fetchAdditionalApplicationsForArea = useCallback(
    async (functionalAreaName: string) => {
      const currentData = completeFunctionalAreasData.find(
        (fa: FunctionalArea) => fa.name === functionalAreaName,
      );

      if (
        !currentData ||
        !currentData.applications ||
        currentData.applications.length !== APPLICATION_PAGE_SIZE
      ) {
        return;
      }

      const hasAdditionalPages =
        currentData.applications.length > APPLICATION_PAGE_SIZE;
      if (hasAdditionalPages) {
        return;
      }

      setIsLoadingAdditionalPages(true);

      try {
        const areaIndex = completeFunctionalAreasData.findIndex(
          (fa: FunctionalArea) => fa.name === functionalAreaName,
        );

        if (areaIndex === -1) return;

        const updatedFunctionalAreas = completeFunctionalAreasData.map(
          (fa: FunctionalArea, index: number) => {
            if (index === areaIndex) {
              return {
                ...fa,
                applications: [...fa.applications],
              };
            }
            return fa;
          },
        );

        let offset = APPLICATION_PAGE_SIZE;
        let hasMore = true;

        while (hasMore) {
          const result = await fetchAdditionalApplications({
            variables: {
              filter: { names: [functionalAreaName] },
              rolesFilter2: {
                roleId: RoleId.DataLead,
              },
              applicationsPagination: {
                limit: APPLICATION_PAGE_SIZE,
                offset: offset,
              },
            },
          });

          const additionalApps =
            result.data?.devhub?.connections?.functionalAreas?.[0]
              ?.applications || [];

          if (additionalApps.length > 0) {
            updatedFunctionalAreas[areaIndex].applications = [
              ...updatedFunctionalAreas[areaIndex].applications,
              ...additionalApps,
            ];

            hasMore = additionalApps.length === APPLICATION_PAGE_SIZE;
            offset += APPLICATION_PAGE_SIZE;
          } else {
            hasMore = false;
          }
        }

        setCompleteFunctionalAreasData(updatedFunctionalAreas);
      } catch (error) {
        // Error fetching additional applications - silently handle
      } finally {
        setIsLoadingAdditionalPages(false);
      }
    },
    [completeFunctionalAreasData, fetchAdditionalApplications],
  );

  /**
   * Fetch applications for a specific functional area (lazy loading)
   */
  const fetchApplicationsForFunctionalArea = useCallback(
    async (functionalAreaName: string) => {
      // Skip if already fetched
      if (fetchedFunctionalAreas.has(functionalAreaName)) {
        return;
      }

      try {
        setIsLoadingAdditionalPages(true);
        const result = await fetchApplications({
          variables: {
            filter: {
              names: [functionalAreaName],
            },
            rolesFilter2: {
              roleId: RoleId.DataLead,
            },
            pagination: null,
            applicationsPagination: {
              limit: APPLICATION_PAGE_SIZE,
              offset: 0,
            },
          },
        });

        if (result.data?.devhub?.connections?.functionalAreas) {
          const newFunctionalAreaData =
            result.data.devhub.connections.functionalAreas[0];
          if (newFunctionalAreaData) {
            // Add to fetched areas set
            setFetchedFunctionalAreas(
              prev => new Set([...prev, functionalAreaName]),
            );

            // Update complete functional areas data
            setCompleteFunctionalAreasData(prev => {
              const existingIndex = prev.findIndex(
                area => area.name === functionalAreaName,
              );
              if (existingIndex >= 0) {
                // Update existing area
                const updated = [...prev];
                updated[existingIndex] = {
                  ...updated[existingIndex],
                  applications: newFunctionalAreaData.applications,
                };
                return updated;
              }
              // Add new area
              return [...prev, newFunctionalAreaData];
            });
          }
        }
      } catch (error) {
        // Error handling - silently handle for now
      } finally {
        // Add a small delay to ensure loading state is visible
        setTimeout(() => {
          setIsFetchingApplicationsForArea(false);
          setIsLoadingAdditionalPages(false);
        }, 300);
      }
    },
    [fetchApplications, fetchedFunctionalAreas],
  );

  // Effect to complete the transition once data is ready
  useEffect(() => {
    if (
      functionalAreaTransition.isTransitioning &&
      functionalAreaTransition.targetArea === selectedFunctionalArea &&
      activeFunctionalAreaApps.length > 0
    ) {
      // Complete the transition
      setTimeout(() => {
        setFunctionalAreaTransition({
          isTransitioning: false,
          targetArea: null,
          currentArea: selectedFunctionalArea,
          dataReady: true,
        });
      }, 100);
    }
  }, [
    functionalAreaTransition.isTransitioning,
    functionalAreaTransition.targetArea,
    selectedFunctionalArea,
    activeFunctionalAreaApps,
  ]);

  // Effect for handling selected functional area changes
  useEffect(() => {
    if (selectedFunctionalArea) {
      // Set loading state immediately if this area hasn't been fetched
      if (!fetchedFunctionalAreas.has(selectedFunctionalArea)) {
        setIsFetchingApplicationsForArea(true);
        setActiveFunctionalAreaApps([]);
      } else {
        // Area already fetched, clear loading state
        setIsFetchingApplicationsForArea(false);
      }
      fetchApplicationsForFunctionalArea(selectedFunctionalArea);
    } else {
      // No area selected, clear loading state
      setIsFetchingApplicationsForArea(false);
    }
  }, [
    selectedFunctionalArea,
    fetchApplicationsForFunctionalArea,
    fetchedFunctionalAreas,
  ]);

  // Effect for auto-selecting single functional area
  useEffect(() => {
    const availableFunctionalAreas =
      data?.devhub?.connections?.functionalAreas || [];
    if (availableFunctionalAreas.length === 1) {
      const areaName = availableFunctionalAreas[0].name;
      // Set loading state immediately for initial selection if not already fetched
      if (!fetchedFunctionalAreas.has(areaName)) {
        setIsFetchingApplicationsForArea(true);
        setActiveFunctionalAreaApps([]);
      }
      setSelectedFunctionalArea(areaName);
    }
  }, [data, fetchedFunctionalAreas]);

  // Effect to populate activeFunctionalAreaApps when data is available for the selected area
  useEffect(() => {
    if (selectedFunctionalArea && completeFunctionalAreasData.length > 0) {
      const selectedAreaData = completeFunctionalAreasData.find(
        area => area.name === selectedFunctionalArea,
      );
      if (selectedAreaData && selectedAreaData.applications) {
        setActiveFunctionalAreaApps(selectedAreaData.applications);
        setIsFetchingApplicationsForArea(false);
      }
    }
  }, [selectedFunctionalArea, completeFunctionalAreasData]);

  return useMemo(
    () => ({
      // State
      selectedFunctionalArea,
      activeFunctionalAreaApps,
      completeFunctionalAreasData,
      fetchedFunctionalAreas,
      functionalAreaTransition,
      isLoadingAdditionalPages,
      isFetchingApplicationsForArea,

      // Transition management
      pendingSwitchToArea,
      showSwitchConfirmDialog,

      // Actions
      performFunctionalAreaTransition,
      handleFunctionalAreaChangeWithConfirmation,
      confirmFunctionalAreaSwitch,
      cancelFunctionalAreaSwitch,
      fetchApplicationsForFunctionalArea,
      fetchAdditionalApplicationsForArea,

      // Setters for external state management
      setSelectedFunctionalArea,
      setActiveFunctionalAreaApps,
      setCompleteFunctionalAreasData,
    }),
    [
      selectedFunctionalArea,
      activeFunctionalAreaApps,
      completeFunctionalAreasData,
      fetchedFunctionalAreas,
      functionalAreaTransition,
      isLoadingAdditionalPages,
      isFetchingApplicationsForArea,
      pendingSwitchToArea,
      showSwitchConfirmDialog,
      performFunctionalAreaTransition,
      handleFunctionalAreaChangeWithConfirmation,
      confirmFunctionalAreaSwitch,
      cancelFunctionalAreaSwitch,
      fetchApplicationsForFunctionalArea,
      fetchAdditionalApplicationsForArea,
      setSelectedFunctionalArea,
      setActiveFunctionalAreaApps,
      setCompleteFunctionalAreasData,
    ],
  );
};
